import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { LabelPrinterPickerProps } from "@/components/label-printer-picker";
import { toast } from "@/hooks/use-toast";
import { openBlobInNewTab } from "@/lib/blob-url";
import { downloadZplDispatcher, resolveAgentDispatcher, type LabelDispatcher } from "@/lib/label-dispatch";
import { fetchMe, savePrinterDefaults, type MeResponse } from "@/lib/me";
import { withCurrentValue } from "@/lib/printer-options";
import { printersApi, type PrinterDto } from "@/lib/printers";

export const MIN_PARCELS = 1;
export const MAX_PARCELS = 999;

/** What the caller will do with the ZPL — the two are the same bytes, not the same event. */
export type LabelZplPurpose = "print" | "download";

export interface UseLabelPrintingOptions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The count the box is (re-)seeded to on every open. */
  initialCount: number;
  /** The no-agent path: the same labels rendered as a PDF, opened in a new tab. */
  fetchPdf: (parcelCount: number) => Promise<Blob>;
  /**
   * The agent path and the download diagnostic.
   *
   * `purpose` exists because a route that records the fetch server-side needs to tell a real
   * dispatch from a diagnostic export; a route that records nothing can ignore it. Passed to
   * the caller rather than decided here, since only the caller knows which of those its
   * endpoint is.
   */
  fetchZpl: (parcelCount: number, purpose: LabelZplPurpose) => Promise<string>;
  /** File name for the download diagnostic. */
  downloadFileName: string;
}

export interface UseLabelPrintingResult {
  /** Held as text so clearing the box leaves it genuinely empty rather than snapping to 0. */
  count: string;
  setCount: (value: string) => void;
  /** The count as a number; only meaningful when `valid`. */
  parsed: number;
  valid: boolean;
  printing: boolean;
  downloading: boolean;
  /**
   * Both footer actions are live: a valid count and nothing already in flight.
   *
   * Derived here rather than in the dialog so the two buttons cannot drift apart — and
   * deliberately NOT gated on the agent probe, which may hang: the PDF path works without it.
   */
  canSubmit: boolean;
  /** The resolved workstation agent, or null for the PDF path. Null while the probe runs. */
  agent: LabelDispatcher | null;
  /**
   * Everything `<LabelPrinterPicker>` needs, grouped rather than loose: these four are only ever
   * splatted into that one component, and returning them as its prop object says so.
   */
  printerPicker: LabelPrinterPickerProps;
  submit: () => Promise<void>;
  downloadZpl: () => Promise<void>;
}

/**
 * Everything the parcel-label dialogs do that is not "which record am I printing for".
 *
 * Shared by the job and invoice dialogs, which differ only in their two API calls and the count
 * they open with. Extracted rather than copied because the interesting parts — the agent probe,
 * the destination guard, the remember-my-printer loop, the dead-agent demotion — are the parts
 * where a second copy would rot silently: both dialogs put labels on real parcels, and a divergence
 * shows up as a print that went somewhere nobody is looking.
 */
export function useLabelPrinting({
  open,
  onOpenChange,
  initialCount,
  fetchPdf,
  fetchZpl,
  downloadFileName,
}: UseLabelPrintingOptions): UseLabelPrintingResult {
  const queryClient = useQueryClient();
  const [count, setCount] = useState(String(initialCount));
  const [printing, setPrinting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [agent, setAgent] = useState<LabelDispatcher | null>(null);
  const [printers, setPrinters] = useState<PrinterDto[] | undefined>(undefined);
  const [printersFailed, setPrintersFailed] = useState(false);
  const [me, setMe] = useState<MeResponse | null>(null);
  // The operator's pick. `undefined` means "hasn't touched it", which is not the same as
  // "chose no printer" (null): only the first defers to the employee's saved default. Deriving
  // the shown value from this instead of seeding state in an effect means a slow /api/me can
  // never overwrite a choice the operator already made.
  const [chosenPrinterId, setChosenPrinterId] = useState<number | null | undefined>(undefined);

  // Re-seed on every open: the record may have changed since this dialog last closed — a job
  // dispatched in the meantime carries a different parcel count.
  useEffect(() => {
    if (open) setCount(String(initialCount));
  }, [open, initialCount]);

  // Resolve the destination once per open. Agents can be installed, stopped or
  // started between opens, so a stale answer would send labels nowhere.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    // Drop the previous open's answer before probing. The dialog is not unmounted
    // on close (the parent owns `open`), so without this the body would name a
    // destination resolved minutes ago and a fast click would dispatch to it —
    // exactly the stale answer the re-probe exists to prevent. Falling back to the
    // PDF wording while the probe runs is not just the safe default, it is the
    // honest one: a click in that window really does take the PDF path.
    setAgent(null);
    void resolveAgentDispatcher()
      .then((found) => {
        if (!cancelled) setAgent(found);
      })
      // resolveAgentDispatcher swallows per-adapter throws so it cannot reject
      // today, but an unhandled rejection here would be silent and one refactor
      // away. No agent is the correct outcome for a probe that blew up.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Only an agent can be pointed at a named queue, so both lookups wait for one: with no agent
  // the labels go to a PDF tab that has no destination to choose, and every real user takes that
  // path until an adapter ships. Fetching regardless would put two requests behind every open of
  // this dialog to populate a control nobody sees.
  useEffect(() => {
    if (!open || !agent) return;
    let cancelled = false;
    setChosenPrinterId(undefined);
    // Both list states reset with the choice, not just the choice: a previous open's failure left
    // isError true across the refetch, so the picker briefly said "(printer list failed to load)"
    // about a list that was loading perfectly well.
    setPrinters(undefined);
    setPrintersFailed(false);
    void printersApi.list().then(
      (list) => {
        if (!cancelled) {
          setPrinters(list);
          setPrintersFailed(false);
        }
      },
      (e: unknown) => {
        // Not fatal: without the list the operator prints to the agent's own default device,
        // which is what happened before this picker existed.
        console.debug("Could not load the printer list; falling back to the agent's default.", e);
        if (!cancelled) {
          setPrinters(undefined);
          setPrintersFailed(true);
        }
      },
    );
    // fetchMe directly, not useAuth(): the ["me"] query is cached with staleTime 60_000, so right
    // after this dialog saves a new default it would still serve the pre-save value and the
    // remember-my-printer loop would look broken. This is the only fetchMe caller outside
    // auth-context for that reason — it is deliberate, not an oversight to "fix".
    void fetchMe().then(
      (found) => {
        if (!cancelled) setMe(found);
      },
      (e: unknown) => {
        console.debug("Could not load the current employee; no saved printer default applied.", e);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [open, agent]);

  const parsed = Number(count);
  const valid = /^\d+$/.test(count.trim()) && parsed >= MIN_PARCELS && parsed <= MAX_PARCELS;

  const savedPrinterId = me?.defaultStickerPrinterId ?? null;
  const selectedPrinterId = chosenPrinterId === undefined ? savedPrinterId : chosenPrinterId;
  const printerOptions = withCurrentValue(
    (printers ?? []).filter((p) => p.labelPrinter),
    { data: printers, isError: printersFailed },
    selectedPrinterId,
    "not a label printer",
  );
  // Resolved from the raw list, never from `printerOptions`: the option withCurrentValue appends
  // carries a "(not a label printer)" suffix for display, and no workstation has a queue by that
  // name.
  const selectedPrinterName = printers?.find((p) => p.printerId === selectedPrinterId)?.printerName;
  // A named printer we cannot send to. Sending no target here would print to the agent's own
  // default queue while the select still reads "Printer #7 (…)", telling the operator one
  // destination and using another, so every one of these states refuses the print.
  const selectedPrinterMissing = selectedPrinterId != null && selectedPrinterName === undefined;
  // …but they are not the same state, and the operator is told which. Until the list request
  // settles, "missing" only means "not looked up yet": showing the permanent warning then made
  // the note contradict a select that read "Loading…", and a click in that window was refused
  // with the wrong branch's "that printer no longer exists".
  const printerListSettled = printers !== undefined || printersFailed;
  const selectedPrinterUnresolved = selectedPrinterMissing && printerListSettled;

  /**
   * Stores the pick as the employee's default, so the next print goes to the same place —
   * legacy's "last printer used wins", and the only way a warehouse that never opens the admin
   * screen ever gets a default at all.
   *
   * Best-effort and never awaited: the labels are already out of the printer by the time this
   * runs, so nothing here may fail the print or hold the dialog open.
   */
  function rememberPrinterChoice() {
    // null is a real choice — "print to whatever the agent decides" — and must stick, or an
    // operator who deliberately switches back gets re-seeded to the old printer on the next open.
    // Only an unchanged value is skipped.
    // `me` is required, not because the server needs it (it resolves the employee from the token)
    // but because this endpoint replaces both columns: the docket id has to be echoed back, and
    // the only copy of it is the /api/me answer fetched when this dialog opened.
    if (!me || selectedPrinterId === savedPrinterId) return;
    void savePrinterDefaults({
      defaultDocketPrinterId: me.defaultDocketPrinterId,
      defaultStickerPrinterId: selectedPrinterId,
    })
      // The app-wide ["me"] query holds the old value for up to its 60s staleTime otherwise, so
      // anything reading the default through useAuth() would disagree with what was just saved.
      .then(() => queryClient.invalidateQueries({ queryKey: ["me"] }))
      .catch((e: unknown) => {
        // Not surfaced: the print already succeeded, and an error toast on a successful print reads
        // as "the labels failed". The next print simply re-offers the same choice.
        console.debug("Could not store the parcel-sticker printer default.", e);
      });
  }

  /** The no-agent path: a PDF in a new tab. Returns with the dialog closed if it got there. */
  async function printViaPdf() {
    const pdf = await fetchPdf(parsed);
    if (!openBlobInNewTab(pdf)) {
      // The server may already have recorded the print, so closing silently here would leave
      // the audit trail claiming a print the operator never saw. Keep the dialog up and say
      // what happened.
      toast.error("Your browser blocked the labels tab. Allow pop-ups for this site, then print again.");
      return;
    }
    onOpenChange(false);
  }

  async function submit() {
    if (agent && selectedPrinterMissing) {
      // Deliberately blocks rather than quietly dropping the target: the operator has been shown a
      // destination, and a print that goes somewhere else is worse than a print that does not
      // happen — the labels come out of an unattended queue with nobody looking for them.
      toast.error(
        !printerListSettled
          ? "The printer list is still loading, so the saved printer can't be confirmed yet. Try again in a moment."
          : printersFailed
            ? "The printer list could not be loaded, so the saved printer can't be confirmed. Choose the agent's default printer to print anyway."
            : "That printer no longer exists. Choose another, or the agent's default printer.",
      );
      return;
    }
    setPrinting(true);
    try {
      if (agent) {
        // Native ZPL straight to the workstation agent: no tab, no dialog, no
        // scaling to get wrong.
        const zpl = await fetchZpl(parsed, "print");
        try {
          // Absent, not "": an adapter handed `printer: ""` cannot tell "the operator chose
          // nothing" from "the operator chose a queue named empty string".
          await agent.print(zpl, selectedPrinterName ? { printer: selectedPrinterName } : undefined);
        } catch (agentError) {
          // The probe answered at open and submit trusted it; a service that stopped in between
          // would otherwise fail every retry identically. Demote to the PDF path so the operator
          // still gets labels, and drop the agent so the dialog stops naming a destination that
          // is gone.
          console.debug(`Label agent "${agent.name}" refused the print; falling back to the PDF path.`, agentError);
          setAgent(null);
          // Informational, not an error: the labels are still coming. Raised as an error toast it
          // stacked with whatever the PDF path then said — a blocked pop-up, or the outer catch —
          // and the operator got two red toasts for one problem, the second of which is the only
          // one they can act on. Kept before the PDF call: that call opens a tab and takes focus.
          toast.info(`${agent.name} did not accept the labels. Opening a PDF instead — print it at actual size.`);
          await printViaPdf();
          return;
        }
        rememberPrinterChoice();
        onOpenChange(false);
        return;
      }

      await printViaPdf();
    } catch (e) {
      // Surface the server's reason — a 403 from a missing claim reads very
      // differently to the operator than a 400 or a 500 would.
      toast.error(`Print parcel labels failed: ${e instanceof Error ? e.message : "Unknown error"}`);
    } finally {
      setPrinting(false);
    }
  }

  /**
   * Saves the raw ZPL instead of printing it.
   *
   * Available with or without an agent: it is how the label output gets onto real
   * hardware before any agent adapter exists — the only way to settle the open
   * questions about stock size, stacking direction and long-address overprint.
   * Leaves the dialog open, because looking at the file is rarely the last step.
   */
  async function downloadZpl() {
    setDownloading(true);
    try {
      const zpl = await fetchZpl(parsed, "download");
      await downloadZplDispatcher.print(zpl, { fileName: downloadFileName });
    } catch (e) {
      toast.error(`Download ZPL failed: ${e instanceof Error ? e.message : "Unknown error"}`);
    } finally {
      setDownloading(false);
    }
  }

  return {
    count,
    setCount,
    parsed,
    valid,
    printing,
    downloading,
    canSubmit: valid && !printing && !downloading,
    agent,
    printerPicker: {
      options: printerOptions,
      value: selectedPrinterId,
      onChange: setChosenPrinterId,
      unresolved: selectedPrinterUnresolved,
    },
    submit,
    downloadZpl,
  };
}
